import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { LoginDetail, LoginRequest, LoginResponse, LoginTotpRequest } from '../models/login.model';
import { EndSessionRequest, EndSessionResponse } from '../models/logout.model';

/**
 * sso-bck è raggiunto sulla stessa origin: in sviluppo tramite il proxy di ng serve,
 * in produzione tramite nginx che serve questo bundle e fa reverse-proxy di /auth e /sso.
 * Questo mantiene il cookie di sessione (ATLANTIS_SSO_SID, SameSite=Lax) same-site
 * senza bisogno di CORS.
 */
@Injectable({ providedIn: 'root' })
export class AuthApiService {
  private http = inject(HttpClient);

  login(body: LoginRequest): Observable<LoginResponse> {
    return this.http.post<LoginResponse>('/api/auth/login', body);
  }

  /** Secondo passaggio del login: il codice dell'app di autenticazione. */
  loginTotp(body: LoginTotpRequest): Observable<LoginResponse> {
    return this.http.post<LoginResponse>('/api/auth/login/totp', body);
  }

  /**
   * Nome e logo dell'applicativo che ha avviato l'accesso, per personalizzare la pagina.
   *
   * Non consuma l'opaque: sso-bck lo legge con `peek`. Un fallimento qui non deve impedire
   * il login — è una rifinitura, non un prerequisito.
   */
  loginDetail(opaque: string): Observable<LoginDetail> {
    return this.http.get<LoginDetail>('/api/auth/login-detail', { params: { opaque } });
  }

  /**
   * Dove mandare il browser per accedere con un provider federato.
   *
   * È una navigazione top-level, non una chiamata: sso-bck risponde con un redirect verso
   * l'IdP e imposta il cookie di `state` che ritroverà al callback.
   */
  federatedStartUrl(accountType: string, opaque: string): string {
    return `/api/auth/federated/${encodeURIComponent(accountType)}/start?opaque=${encodeURIComponent(opaque)}`;
  }

  /**
   * Passo di conferma di RP-Initiated Logout: dice a sso-bck se chiudere la sessione e
   * riceve indietro dove mandare il browser.
   *
   * Non richiede una sessione valida: una sessione già scaduta deve comunque poter
   * riportare l'utente alla pagina di atterraggio dell'applicativo invece di un 401.
   */
  endSession(body: EndSessionRequest): Observable<EndSessionResponse> {
    return this.http.post<EndSessionResponse>('/api/auth/logout/end-session', body);
  }
}
